import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './lib/auth-context';
import { initSentry } from './lib/sentry';
import { queryClient } from './lib/queryClient';
import Router from './Router';
import './index.css';

initSentry();

console.log('[BOOT] main.tsx loaded');
window.addEventListener('error', (e) => console.error('[BOOT ERROR]', e.error || e.message));
window.addEventListener('unhandledrejection', (e) => console.error('[BOOT PROMISE ERROR]', e.reason));

const rootElement = document.getElementById('root');
const bootDiagnostics = (window as Window & {
  __BIZSHERPA_BOOT__?: {
    markMounted?: () => void;
    fail?: (reason: string, detail?: Record<string, unknown>) => void;
  };
}).__BIZSHERPA_BOOT__;

function AppErrorFallback({ resetError }: { resetError: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-8">
      <div className="text-center">
        <h1 className="text-xl font-semibold text-gray-900">Something went wrong</h1>
        <p className="mt-2 text-sm text-gray-600">
          Our team has been notified. Please try again.
        </p>
        <button
          onClick={resetError}
          className="mt-4 rounded bg-blue-600 px-4 py-2 text-sm text-white"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

function unregisterBlockingServiceWorkers() {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  navigator.serviceWorker.getRegistrations()
    .then((registrations) =>
      Promise.all(
        registrations
          .filter((registration) => registration.active?.scriptURL?.includes('/sw.js'))
          .map(async (registration) => {
            const cacheKeys = await caches.keys().catch(() => []);
            await Promise.all(cacheKeys.map((key) => caches.delete(key).catch(() => false)));
            return registration.unregister();
          }),
      ),
    )
    .catch((error) => {
      console.warn('Failed to unregister legacy service workers:', error);
    });
}

if (!rootElement) {
  document.body.innerHTML = '<div style="padding: 20px; font-family: sans-serif;"><h1>Error: Root element not found</h1><p>The #root div is missing from the HTML.</p></div>';
} else {
  try {
    console.log('[BOOT] rendering React app');
    createRoot(rootElement).render(
      <StrictMode>
        <Sentry.ErrorBoundary fallback={({ resetError }) => <AppErrorFallback resetError={resetError} />}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <Router />
            </AuthProvider>
          </QueryClientProvider>
        </Sentry.ErrorBoundary>
      </StrictMode>,
    );
    unregisterBlockingServiceWorkers();
    bootDiagnostics?.markMounted?.();
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    bootDiagnostics?.fail?.('react-bootstrap-failed', { message: errorMessage });
    rootElement.innerHTML = `<div style="padding: 20px;"><h1>Error loading app</h1><pre>${errorMessage}</pre></div>`;
  }
}
